/**
 * Action Data Form Modal
 * Wrapper around GenerateSchemaForm for actions requiring data input
 */

import React from 'react';
import { View, Text, Pressable, Modal, Platform } from 'react-native';
import { AntDesign } from '@expo/vector-icons';
import GenerateSchemaForm from '@hooks/generateSchemaForm';
import { ActionTemplate } from '@utils/semanticActionMapper';
import { themeSelector } from '@reduxLocal/selectors/themeSelector';
import { formatActionDescriptionForEasyMode } from '@utils/easyModeActionFormatter';

interface ActionDataFormProps {
  visible: boolean;
  actionTemplate: ActionTemplate | null;
  onSubmit: (template: ActionTemplate, data: any) => void;
  onCancel: () => void;
  initialData?: Record<string, any>;
}

const ActionDataForm: React.FC<ActionDataFormProps> = ({
  visible,
  actionTemplate,
  onSubmit,
  onCancel,
  initialData
}) => {
  const theme = themeSelector();
  const formRef = React.useRef(null);

  if (!actionTemplate || !visible) {
    return null;
  }

  const handleSubmit = async (values: any) => {
    onSubmit(actionTemplate, values);
    return Promise.resolve({ success: true });
  };

  const handleCustomSubmit = () => {
    const formValues = formRef.current?.values;
    if (formValues) {
      onSubmit(actionTemplate, formValues);
    }
  };

  return (
    <Modal
      visible={visible}
      transparent
      animationType="fade"
      onRequestClose={onCancel}
    >
      <View style={{
        flex: 1,
        justifyContent: 'center',
        alignItems: 'center',
        backgroundColor: 'rgba(0, 0, 0, 0.5)',
        padding: 20
      }}>
        <View style={{
          backgroundColor: theme ? '#ffffff' : '#1e293b',
          borderRadius: 16,
          padding: 24,
          minWidth: Platform.OS === 'web' ? 500 : '90%',
          maxWidth: Platform.OS === 'web' ? 600 : '95%',
          maxHeight: '80%',
          shadowColor: '#000',
          shadowOffset: { width: 0, height: 4 },
          shadowOpacity: 0.3,
          shadowRadius: 12,
          elevation: 8
        }}>
          {/* Header */}
          <View style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            alignItems: 'center',
            marginBottom: 20
          }}>
            <View style={{ flex: 1 }}>
              <Text style={{
                fontSize: 20,
                fontWeight: '700',
                color: theme ? '#1f2937' : '#f3f4f6',
                marginBottom: 4
              }}>
                {formatActionDescriptionForEasyMode(actionTemplate.description || actionTemplate.intent, actionTemplate.intent)}
              </Text>
              <Text style={{
                fontSize: 14,
                color: theme ? '#6b7280' : '#9ca3af'
              }}>
                Please provide the required information
              </Text>
            </View>

            <Pressable
              onPress={onCancel}
              style={({ pressed }) => ({
                padding: 8,
                borderRadius: 8,
                backgroundColor: pressed ? (theme ? '#f3f4f6' : '#374151') : 'transparent'
              })}
            >
              <AntDesign name="close" size={20} color={theme ? '#6b7280' : '#9ca3af'} />
            </Pressable>
          </View>

          {/* Form */}
          <View style={{
            flex: 1,
            minHeight: 200
          }}>
            {actionTemplate.schema && (
              <GenerateSchemaForm
                ref={formRef}
                schema={actionTemplate.schema}
                submitFunction={handleSubmit}
                buttonLabel={actionTemplate.schemaConfig?.buttonLabel || "Submit"}
                columns={actionTemplate.schemaConfig?.columns}
                staged={actionTemplate.schemaConfig?.staged}
                hasTitle={actionTemplate.schemaConfig?.hasTitle}
                initialData={initialData}
                scrollEnabled={true}
                hideButton={true}
                customStyling={{
                  backgroundColor: 'transparent',
                  padding: 0
                }}
              />
            )}
          </View>

          {/* Footer */}
          <View style={{
            flexDirection: 'row',
            justifyContent: 'space-between',
            gap: 12,
            marginTop: 20,
            paddingTop: 16,
            borderTopWidth: 1,
            borderTopColor: theme ? '#e5e7eb' : '#374151'
          }}>
            <Pressable
              onPress={onCancel}
              style={({ pressed }) => ({
                paddingHorizontal: 20,
                paddingVertical: 10,
                borderRadius: 8,
                backgroundColor: pressed ? (theme ? '#f3f4f6' : '#374151') : (theme ? '#e5e7eb' : '#1f2937'),
                borderWidth: 1,
                borderColor: theme ? '#d1d5db' : '#4b5563'
              })}
            >
              <Text style={{
                fontSize: 14,
                fontWeight: '600',
                color: theme ? '#374151' : '#d1d5db'
              }}>
                Cancel
              </Text>
            </Pressable>

            <Pressable
              onPress={handleCustomSubmit}
              style={({ pressed }) => ({
                paddingHorizontal: 24,
                paddingVertical: 10,
                borderRadius: 8,
                backgroundColor: pressed ? '#1d4ed8' : '#2563eb',
                borderWidth: 1,
                borderColor: '#1d4ed8'
              })}
            >
              <Text style={{
                fontSize: 14,
                fontWeight: '600',
                color: '#ffffff'
              }}>
                {actionTemplate.schemaConfig?.buttonLabel || "Create Project"}
              </Text>
            </Pressable>
          </View>
        </View>
      </View>
    </Modal>
  );
};

export default ActionDataForm;

